import Styles from './TemplateCarousel.module.scss'
import { useRef } from 'react'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { DEFAULT_OPTIONS, InnerCarousel } from 'components/Carousel/Carousel'
import { collectResults } from 'utils/query'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ASPECT_RATIOS, AspectRatioKey } from 'components/NewFileModal/Types'
import useEmblaCarousel from 'embla-carousel-react'
import { TemplateCarouselProps } from './Types'
import { TemplateButton } from '../TemplateButton/TemplateButton'
import { useModals } from 'almanac-ui/Modal'
import { TemplateModal } from 'components/Templates/TemplateModal'
import { ITEM_HEIGHT, ITEM_WIDTH, WIDE_ASPECT_RATIO } from './constants'
import { PreviousButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/PreviousButton'
import { NextButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/NextButton'
import { generatePath, Link } from 'react-router-dom'
import { TEMPLATES_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ChevronRightSmall } from 'blaze-ui/icons/24'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { Text } from 'blaze-ui/Text/Text'

export const PopularTemplateCarousel = ({ heading }: { heading: string }) => {
  const { openModal, closeModal } = useModals()
  const {
    drawersStore: { closeDrawer },
  } = useRootStore()
  const { isMobile } = useResponsiveDesign()

  const ITEMS_TO_LOAD = 9
  const elementRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersectionObserver(elementRef, { threshold: [0, 0.00000001] })
  const isVisible = (intersection?.intersectionRatio ?? 0) >= 0.00000001

  const visualTemplatesQuery = useVisualTemplates(
    {
      element_type: 'Template',
      order_by: 'popular',
      items: ITEMS_TO_LOAD,
    },
    {
      enabled: isVisible,
      cacheTime: 2 * 1000 * 60,
      staleTime: Infinity,
    },
  )

  const templates = collectResults(visualTemplatesQuery, (data) => data.visual_elements)
  const embla = useEmblaCarousel(DEFAULT_OPTIONS)
  const emblaApi = embla[1]

  if (visualTemplatesQuery.isFetched && !visualTemplatesQuery.isLoading && templates.length === 0) {
    return <></>
  }

  return (
    <div className={Styles.carouselContainer}>
      <div className={Styles.popularCarouselContainer}>
        <div className={Styles.title}>
          <Heading level={isMobile ? 5 : 3}>{heading}</Heading>
          {!isMobile && emblaApi && templates.length > 1 && (
            <div className={Styles.controls}>
              <PreviousButton emblaApi={emblaApi} />
              <NextButton emblaApi={emblaApi} />
            </div>
          )}
        </div>
        <div ref={elementRef} className={Styles.carousel}>
          <InnerCarousel embla={embla} withControls={false} paddingRight="18px">
            {(!visualTemplatesQuery.isFetched || visualTemplatesQuery.isLoading) &&
              Array.from({ length: ITEMS_TO_LOAD }).map((_, i) => (
                <ShimmeringColor aspectRatio={1} width={150} height={150} key={i} />
              ))}
            {!visualTemplatesQuery.isLoading &&
              templates.map((template, i) => {
                const isWide = template.width / template.height > WIDE_ASPECT_RATIO
                const itemWidth = isWide ? undefined : ITEM_WIDTH
                const itemHeight = isWide ? ITEM_HEIGHT : undefined

                const defaultAspectRatioKey = Object.entries(ASPECT_RATIOS).filter(
                  ([_, designAspectRatio]) =>
                    designAspectRatio.width === template.width && designAspectRatio.height === template.height,
                )?.[0]?.[0] as AspectRatioKey

                return (
                  <div key={`${template.id}-${i}`} className={Styles.popularTemplateButtonContainer}>
                    <Text>
                      <div className={Styles.popularRank}>{i + 1}</div>
                    </Text>
                    <TemplateButton
                      template={template}
                      onPress={() => {
                        closeDrawer()
                        openModal(TemplateModal, {
                          initialTemplateId: template.id,
                          onPressOutside: closeModal,
                          initialAspectRatioKey: defaultAspectRatioKey,
                          isAspectRatioKeyEditable: true,
                        })
                      }}
                      aspectRatio={template.width / template.height}
                      itemWidth={itemWidth}
                      itemHeight={itemHeight}
                      ignoreAspectRatio
                      withName={false}
                    />
                  </div>
                )
              })}
          </InnerCarousel>
        </div>
      </div>
    </div>
  )
}

export const TemplateCarousel = ({ heading, aspectRatioKey, templateStyle, templateType }: TemplateCarouselProps) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { openModal, closeModal } = useModals()
  const {
    drawersStore: { closeDrawer },
  } = useRootStore()
  const { isMobile } = useResponsiveDesign()

  const { width, height } = ASPECT_RATIOS[aspectRatioKey]

  const isWide = width / height > WIDE_ASPECT_RATIO

  const itemWidth = isWide ? undefined : ITEM_WIDTH
  const itemHeight = isWide ? ITEM_HEIGHT : undefined

  const ITEMS_TO_LOAD = 10
  const elementRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersectionObserver(elementRef, { threshold: [0, 0.00000001] })
  const isVisible = (intersection?.intersectionRatio ?? 0) >= 0.00000001

  const visualTemplatesQuery = useVisualTemplates(
    {
      height,
      width,
      element_type: 'Template',
      order_by: 'random',
      items: ITEMS_TO_LOAD,
      style: templateStyle,
      type: templateType,
      additional_seed: aspectRatioKey,
    },
    {
      enabled: isVisible,
      cacheTime: 2 * 1000 * 60,
      staleTime: Infinity,
    },
  )

  const templates = collectResults(visualTemplatesQuery, (data) => data.visual_elements)
  const { fetchNextPage, hasNextPage } = visualTemplatesQuery
  const embla = useEmblaCarousel(DEFAULT_OPTIONS)
  const emblaApi = embla[1]

  if (visualTemplatesQuery.isFetched && !visualTemplatesQuery.isLoading && templates.length === 0) {
    return <></>
  }

  return (
    <div className={Styles.carouselContainer}>
      <div className={Styles.title}>
        <Link
          to={{
            pathname: generatePath(TEMPLATES_PATH, { workspaceId: currentWorkspaceId }),
            state: {
              initialAspectRatioKey: aspectRatioKey,
              initialStyles: templateStyle,
            },
          }}
          className={Styles.header}
          onClick={() => {
            WorkspaceService.hideSidebar()
            closeDrawer()
            closeModal()
          }}
        >
          <Heading level={isMobile ? 5 : 4}>{heading}</Heading>
          <ChevronRightSmall color="var(--dark-80)" />
        </Link>
        {!isMobile && emblaApi && templates.length > 1 && (
          <div className={Styles.controls}>
            <PreviousButton emblaApi={emblaApi} />
            <NextButton emblaApi={emblaApi} />
          </div>
        )}
      </div>
      <div ref={elementRef} className={Styles.carousel}>
        <InnerCarousel embla={embla} withControls={false}>
          {(!visualTemplatesQuery.isFetched || visualTemplatesQuery.isLoading) &&
            Array.from({ length: 10 }).map((_, i) => (
              <ShimmeringColor aspectRatio={width / height} width={itemWidth} height={itemHeight} key={i} />
            ))}
          {!visualTemplatesQuery.isLoading &&
            templates.map((template, i) => {
              return (
                <TemplateButton
                  key={`${template.id}-${i}`}
                  template={template}
                  onPress={() => {
                    closeDrawer()
                    openModal(TemplateModal, {
                      initialTemplateId: template.id,
                      onPressOutside: closeModal,
                      initialAspectRatioKey: aspectRatioKey,
                      isAspectRatioKeyEditable: true,
                    })
                  }}
                  aspectRatio={template.width / template.height}
                  itemWidth={itemWidth}
                  itemHeight={itemHeight}
                />
              )
            })}
          {hasNextPage && (
            <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage}>
              <ShimmeringColor aspectRatio={width / height} width={itemWidth} height={itemHeight} />
            </InfiniteScroll>
          )}
          {hasNextPage &&
            Array.from({ length: 2 }).map((_, i) => (
              <ShimmeringColor key={i} aspectRatio={width / height} width={itemWidth} height={itemHeight} />
            ))}
        </InnerCarousel>
      </div>
    </div>
  )
}
